// One photo per product. Only the next photo is fetched, and only while the tile is visible after a scroll.
class CollectionPhotos extends HTMLElement {
  #ac; #observer; #timer; #items = []; #index = 0;
  #visible = false; #scrolled = false; #paused = false; #focused = false; #busy = false;
  #img; #link; #name; #button; #motion;
  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#items = JSON.parse(this.querySelector('[data-photos]').textContent);
    this.#img = this.querySelector('[data-photo]');
    this.#link = this.querySelector('[data-photo-link]');
    this.#name = this.querySelector('[data-photo-name]');
    this.#button = this.querySelector('[data-toggle]');
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#scrolled = window.scrollY > 0;
    if (this.#items.length < 2) { this.#button.hidden = true; return; }
    this.#paused = navigator.connection?.saveData === true;
    this.#updateButton();
    window.addEventListener('scroll', () => { this.#scrolled = true; this.#schedule(); }, { ...opts, passive: true });
    document.addEventListener('visibilitychange', () => this.#schedule(), opts);
    this.#motion.addEventListener('change', () => this.#schedule(), opts);
    this.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, opts);
    this.addEventListener('focusout', (event) => { this.#focused = this.contains(event.relatedTarget); this.#schedule(); }, opts);
    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle') return;
      this.#paused = !this.#paused;
      this.#updateButton();
      this.#schedule();
    }, opts);
    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries[0].isIntersecting;
      this.#schedule();
    }, { threshold: 0.5 });
    this.#observer.observe(this);
  }
  #updateButton() {
    this.#button.textContent = this.#paused ? this.#button.dataset.playLabel : this.#button.dataset.pauseLabel;
    this.#button.setAttribute('aria-pressed', String(this.#paused));
  }
  #canRun() {
    return this.isConnected && this.#visible && this.#scrolled && !this.#paused && !this.#focused && !document.hidden && !this.#motion.matches;
  }
  #schedule() {
    // Keep an already-running countdown so scrolling never resets the rotation.
    if (!this.#canRun()) { clearTimeout(this.#timer); this.#timer = null; return; }
    if (this.#timer || this.#busy) return;
    this.#timer = setTimeout(() => { this.#timer = null; this.#advance(); }, 1000);
  }
  async #advance() {
    if (!this.#canRun()) return;
    this.#busy = true;
    const index = (this.#index + 1) % this.#items.length;
    const item = this.#items[index];
    const next = new Image();
    next.src = item.image;
    try {
      await next.decode();
      if (this.#canRun()) {
        this.#img.src = item.image;
        this.#img.alt = item.title;
        this.#link.href = item.url;
        this.#name.textContent = item.title;
        this.#index = index;
      }
    } catch {
      // Skip broken images on the next turn without blanking the current photo.
      this.#index = index;
    } finally {
      this.#busy = false;
      this.#schedule();
    }
  }
  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#observer?.disconnect();
    this.#ac?.abort();
  }
}
if (!customElements.get('s-collection-photos')) customElements.define('s-collection-photos', CollectionPhotos);
